import { Box } from "@mui/material";
import { ReactNode } from "react";

import { ScoutToolPart } from "../hooks/useScoutChatWithMocks";

export type ToolRenderContext = {
  tool: ScoutToolPart;
  /** True when the tool has been dispatched but output isn't available yet. */
  isRunning: boolean;
};

export type ToolRenderer = (ctx: ToolRenderContext) => ReactNode;

function humanize(toolName: string): string {
  const spaced = toolName.replace(/[_-]+/g, " ").trim();
  if (!spaced) return toolName;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Fallback for any tool that doesn't have a custom renderer.
 * Uses the backend-provided `displayName` if present, else humanizes `toolName`.
 */
const genericRenderer: ToolRenderer = ({ tool, isRunning }) => {
  const label = tool.toolDisplayName ?? humanize(tool.toolName);
  return isRunning ? `${label}…` : label;
};

function Underline({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ textDecoration: "underline" }}>
      {children}
    </Box>
  );
}

function readNumber(value: unknown, key: string): number | null {
  if (value && typeof value === "object" && key in value) {
    const v = (value as Record<string, unknown>)[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

const toolRenderers: Record<string, ToolRenderer> = {
  query_facilities: ({ tool, isRunning }) => {
    const count = readNumber(tool.toolOutput, "count") ?? readNumber(tool.toolOutput, "rows");
    const verb = isRunning ? "Searching" : "Searched";
    if (count === null) return `${verb} your facilities`;
    return (
      <>
        {verb} your <Underline>{count.toLocaleString()} facilities</Underline>
      </>
    );
  },
};

export function renderToolTitle(ctx: ToolRenderContext): ReactNode {
  const renderer = toolRenderers[ctx.tool.toolName] ?? genericRenderer;
  return renderer(ctx);
}
